import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { X, Check, Pencil, Camera, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import CountrySelect from './CountrySelect';
import api from '../utils/axiosConfig';

const StravaIcon = ({ className }) => (
  <svg viewBox='0 0 24 24' className={className}>
    <path d='M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169' />
  </svg>
);

export default function ProfileModal({ isOpen, onClose }) {
  const { t } = useLanguage();
  const { user, updateProfile, fetchProfile } = useAuth();
  const overlayRef = useRef(null);
  const fileInputRef = useRef(null);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [stravaConnected, setStravaConnected] = useState(false);
  const [syncingStrava, setSyncingStrava] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setEditingName(false);
    setNameValue(user?.name || '');
    setSyncingStrava(false);
    api.get('/api/strava/status')
      .then(({ data }) => setStravaConnected(!!data?.connected))
      .catch(() => setStravaConnected(false));
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose, user?.name]);

  if (!isOpen) return null;

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const handleSaveName = async () => {
    const trimmed = nameValue.trim();
    if (trimmed.length < 2 || trimmed === user?.name) {
      setEditingName(false);
      setNameValue(user?.name || '');
      return;
    }
    setSaving(true);
    try {
      await updateProfile({ name: trimmed });
      setEditingName(false);
    } catch {
      setNameValue(user?.name || '');
      setEditingName(false);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      await api.post('/api/auth/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchProfile();
    } catch {
      // silently fail — user can retry
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSyncStravaAvatar = async () => {
    setSyncingStrava(true);
    try {
      await api.post('/api/strava/sync-avatar');
      await fetchProfile();
    } catch {
      // silently fail — user can retry
    } finally {
      setSyncingStrava(false);
    }
  };

  const memberSince = user?.createdAt
    ? new Date(user.createdAt).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

  return createPortal(
    <div
      ref={overlayRef}
      className='fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200'
      style={{ zIndex: 10000 }}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div
        className='bg-card border border-border rounded-xl shadow-2xl flex flex-col animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 max-w-[90vw] w-[440px] max-h-[90vh]'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-center justify-between px-5 py-4 border-b border-border shrink-0'>
          <h2 className='text-base font-semibold text-foreground'>
            {t('nav.profile')}
          </h2>
          <Button variant='ghost' size='icon' className='h-8 w-8' onClick={onClose}>
            <X className='h-4 w-4' />
          </Button>
        </div>

        <div className='p-6 space-y-5 overflow-y-auto min-h-0'>
          <div className='flex items-center gap-4'>
            <div className='relative group'>
              <Avatar className='h-16 w-16'>
                {user?.avatarUrl && (
                  <AvatarImage src={user.avatarUrl} alt={user.name} />
                )}
                <AvatarFallback className='bg-primary text-primary-foreground text-lg font-semibold'>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <button
                type='button'
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingAvatar}
                className='absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer'
                title={t('profile.changePhoto')}
              >
                {uploadingAvatar ? (
                  <Loader2 className='h-5 w-5 text-white animate-spin' />
                ) : (
                  <Camera className='h-5 w-5 text-white' />
                )}
              </button>
              <input
                ref={fileInputRef}
                type='file'
                accept='image/jpeg,image/png,image/webp'
                className='hidden'
                onChange={handleAvatarChange}
              />
            </div>
            <div className='flex-1 min-w-0'>
              {editingName ? (
                <div className='flex items-center gap-2'>
                  <Input
                    autoFocus
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveName();
                      if (e.key === 'Escape') {
                        setEditingName(false);
                        setNameValue(user?.name || '');
                      }
                    }}
                    className='h-8 text-sm'
                    disabled={saving}
                  />
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-8 w-8 shrink-0'
                    onClick={handleSaveName}
                    disabled={saving}
                  >
                    <Check className='h-4 w-4' />
                  </Button>
                </div>
              ) : (
                <div className='flex items-center gap-2'>
                  <h3 className='text-lg font-semibold text-foreground truncate'>
                    {user?.name}
                  </h3>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='h-7 w-7 shrink-0'
                    onClick={() => {
                      setNameValue(user?.name || '');
                      setEditingName(true);
                    }}
                    title={t('profile.editName')}
                  >
                    <Pencil className='h-3.5 w-3.5' />
                  </Button>
                </div>
              )}
              <p className='text-sm text-muted-foreground truncate'>{user?.email}</p>
            </div>
          </div>

          {stravaConnected && (
            <Button
              variant='outline'
              size='sm'
              className='w-full gap-2 text-xs'
              onClick={handleSyncStravaAvatar}
              disabled={syncingStrava}
            >
              {syncingStrava ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <StravaIcon className='h-3.5 w-3.5 fill-[#FC4C02]' />
              )}
              {t('profile.useStravaPhoto')}
            </Button>
          )}

          <Separator />

          <div className='space-y-4'>
            <div className='flex items-center justify-between'>
              <Label className='text-sm text-muted-foreground'>{t('profile.roles')}</Label>
              <div className='flex gap-1.5'>
                {user?.roles?.map((role) => (
                  <Badge key={role} variant='secondary' className='capitalize text-xs'>
                    {role}
                  </Badge>
                ))}
              </div>
            </div>

            <div className='space-y-1.5'>
              <Label className='text-sm text-muted-foreground'>{t('profile.country')}</Label>
              <CountrySelect
                value={user?.countryId ?? null}
                onChange={(id) => updateProfile({ countryId: id })}
                placeholder={t('profile.country')}
                popoverClassName='z-[10001]'
              />
            </div>

            <div className='space-y-1.5'>
              <Label htmlFor='profile-birthdate' className='text-sm text-muted-foreground'>
                {t('profile.birthdate')}
              </Label>
              <Input
                id='profile-birthdate'
                type='date'
                value={user?.birthdate ?? ''}
                onChange={(e) => updateProfile({ birthdate: e.target.value || null })}
              />
            </div>

            <div className='flex items-center justify-between'>
              <Label className='text-sm text-muted-foreground'>{t('profile.memberSince')}</Label>
              <span className='text-sm text-foreground'>{memberSince}</span>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
